import { createSlice } from "@reduxjs/toolkit";
import * as promise from "bluebird";

import {
  executeCreateDownloadZipFilePath,
  executeCreateFolder,
  executeDeleteFile,
  executeDownloadFile,
  executeListFiles,
  executeUploadFile,
} from "../api/FileApi";
import { storeKeyName } from "../common/AppConstant";
import { isFolder } from "../common/AppUtil";
import { checkPromiseMapError, handleError } from "../common/ErrorUtil";

/**
 * Slice.
 *
 * @type {object}
 */
export const slice = createSlice({
  // Slice名
  name: storeKeyName.file,

  // 初期State
  initialState: {
    // 読み込み中フラグ（true: 表示、false: 非表示）
    isLoading: false,
    // エラー内容
    error: null,
    // ファイルプレフィックス
    filePrefix: "",
    // ファイル情報一覧
    fileInfos: [],
    // 選択済みファイル情報一覧
    selectedFileInfos: [],
  },

  // Reducer
  reducers: {
    // 通信開始
    fetchStart(state) {
      state.isLoading = true;
    },
    // 通信失敗
    fetchFailure(state, action) {
      state.error = action.payload;
      state.isLoading = false;
    },
    // 通信成功
    fetchSuccess(state, action) {
      state.error = null;
      state.fileInfos = action.payload;
      state.selectedFileInfos = [];
      state.isLoading = false;
    },
    // クリア
    fetchClear(state) {
      state.error = null;
      state.filePrefix = "";
      state.fileInfos = [];
      state.selectedFileInfos = [];
      state.isLoading = false;
    },
    // ファイルプレフィックスの変更
    fetchChangeFilePrefix(state, action) {
      state.filePrefix = action.payload;
    },
    // 選択済みファイル情報の変更
    fetchChangeSelectedFileInfo(state, action) {
      const { fileInfo, checked } = action.payload;

      if (checked) {
        // 選択済みファイル情報一覧に追加
        state.selectedFileInfos = [...state.selectedFileInfos, fileInfo];
      } else {
        // 選択済みファイル情報一覧から削除
        const newSelectedFileInfos = state.selectedFileInfos.filter(
          (selectedFileInfo) => selectedFileInfo.fileKey !== fileInfo.fileKey
        );
        state.selectedFileInfos = newSelectedFileInfos;
      }
    },
    // 選択済みファイル情報一覧の変更
    fetchChangeSelectedFileInfos(state, action) {
      const { fileInfos, checked } = action.payload;

      if (checked) {
        // 選択済みファイル情報一覧に追加
        state.selectedFileInfos = [...fileInfos];
      } else {
        // 選択済みファイル情報一覧から削除
        state.selectedFileInfos = [];
      }
    },
    // ファイル情報一覧のソート
    fetchSortFileInfos(state, action) {
      const { column, nextOrder } = action.payload;

      const sortRule = { asc: [1, -1], desc: [-1, 1] };
      const sortFileInfos = state.fileInfos.sort((a, b) => {
        if (isFolder(a.fileType) && !isFolder(b.fileType)) {
          return -1;
        }
        if (!isFolder(a.fileType) && isFolder(b.fileType)) {
          return 1;
        }
        if (a[column] > b[column]) {
          return sortRule[nextOrder][0];
        }
        if (a[column] < b[column]) {
          return sortRule[nextOrder][1];
        }
        return 0;
      });
      state.fileInfos = sortFileInfos;
    },
    fetchEndCreateDownloadZipFilePath(state) {
      state.error = null;
      state.selectedFileInfos = [];
      state.isLoading = false;
    },
  },
});

/**
 * Actions.
 *
 * @type {object}
 */
export const {
  fetchStart,
  fetchFailure,
  fetchSuccess,
  fetchClear,
  fetchChangeFilePrefix,
  fetchChangeSelectedFileInfo,
  fetchChangeSelectedFileInfos,
  fetchSortFileInfos,
  fetchEndCreateDownloadZipFilePath,
} = slice.actions;

/**
 * Selectors.
 *
 * @type {function}
 */
export const selector = (stateAll) =>
  // すべてのStoreのStateが含まれているため、特定のStoreのStateのみ返す
  stateAll[storeKeyName.file];

/**
 * fetchListFiles.
 *
 */
export const fetchListFiles = (prefix) => async (dispatch) => {
  // 通信開始
  dispatch(fetchStart());

  // APIの実行
  const { data, error } = await executeListFiles(prefix).catch(handleError);
  if (error) {
    dispatch(fetchFailure(error));
    return;
  }

  dispatch(fetchSuccess(data.fileInfos));
};

/**
 * fetchDownloadFile.
 *
 */
export const fetchDownloadFile = (fileInfos) => async (dispatch) => {
  const responses = await promise
    .map(fileInfos, executeDownloadFile, {
      concurrency: 5,
    })
    .catch(handleError);

  const promiseMapError = checkPromiseMapError(responses);
  if (promiseMapError) {
    dispatch(fetchFailure(promiseMapError));
  }
};

/**
 * fetchUploadFile.
 *
 */
export const fetchUploadFile = (filePrefix, files) => async (dispatch) => {
  // 通信開始
  dispatch(fetchStart());

  const uploadFiles = files.map((file) => ({ file, filePrefix }));
  const responses = await promise
    .map(uploadFiles, executeUploadFile, {
      concurrency: 5,
    })
    .catch(handleError);

  const promiseMapError = checkPromiseMapError(responses);
  if (promiseMapError) {
    dispatch(fetchFailure(promiseMapError));
    return;
  }

  // ファイル一覧取得APIの実行
  dispatch(fetchListFiles(filePrefix));
};

/**
 * fetchDeleteFile.
 *
 */
export const fetchDeleteFile = (filePrefix, fileInfos) => async (dispatch) => {
  // 通信開始
  dispatch(fetchStart());

  const responses = await promise
    .map(fileInfos, executeDeleteFile, {
      concurrency: 5,
    })
    .catch(handleError);

  const promiseMapError = checkPromiseMapError(responses);
  if (promiseMapError) {
    dispatch(fetchFailure(promiseMapError));
    return;
  }

  dispatch(fetchListFiles(filePrefix));
};

/**
 * fetchCreateDownloadZipFilePath.
 *
 */
export const fetchCreateDownloadZipFilePath = (folderInfos, label) => async (
  dispatch
) => {
  // 通信開始
  dispatch(fetchStart());

  // 複数の引数を別キー扱いで渡すために、ラップ関数を作成
  const wrappedExecuteCreateDownloadZipFilePath = (folderInfo) =>
    executeCreateDownloadZipFilePath(folderInfo.fileKey, label);

  // ラップ関数を経由してAPIの実行
  const responses = await promise
    .map(folderInfos, wrappedExecuteCreateDownloadZipFilePath, {
      concurrency: 5,
    })
    .catch(handleError);

  const promiseMapError = checkPromiseMapError(responses);
  if (promiseMapError) {
    dispatch(fetchFailure(promiseMapError));
    return;
  }

  dispatch(fetchEndCreateDownloadZipFilePath());
};

/**
 * fechCreateFolder.
 *
 */
export const fechCreateFolder = (filePrefix, folderName) => async (
  dispatch
) => {
  // 通信開始
  dispatch(fetchStart());

  const fileKey = filePrefix + folderName;

  // APIの実行
  const { error } = await executeCreateFolder(fileKey).catch(handleError);
  if (error) {
    dispatch(fetchFailure(error));
    return;
  }

  dispatch(fetchListFiles(filePrefix));
};
